
import { Interview } from '@/types/interview';
import { InterviewManagementFacade } from '@/services/interview';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { InterviewFormValues } from '../schemas/interviewFormSchema';

export const useFormSubmitUtils = () => {
  const queryClient = useQueryClient();
  
  const logFormValues = (prefix: string, values: Partial<InterviewFormValues>) => {
    // Ensure tags is a proper array for logging
    const tags = Array.isArray(values.tags) ? [...values.tags] : [];
    
    console.log(`[${prefix}] Form values:`, {
      ...values,
      hasPhotoUrl: !!values.photoUrl,
      photoUrl: values.photoUrl || 'No photo URL',
      hasContextUrl: !!values.contextUrl,
      contextUrl: values.contextUrl || 'No context URL',
      timestamp: values.timestamp,
      tags: JSON.stringify(tags),
      tagsType: typeof tags,
      isTagsArray: Array.isArray(tags)
    });
  };
  
  const collectFieldValues = (fieldName: string, values: any, formValue: any) => {
    // Collect values from hidden fields
    const hiddenField = document.getElementById(`${fieldName}Hidden`) as HTMLInputElement;
    const hiddenValue = hiddenField?.value;
    
    const isExplicitEmpty = (v: any) => typeof v === 'string' && v.trim() === '';
    
    console.log(`[useFormSubmitUtils] Available ${fieldName} sources:`, {
      fromValues: values,
      fromFormGetValues: formValue,
      fromHiddenField: hiddenValue
    });
    
    // If user explicitly cleared the field, respect that (do NOT fallback)
    if (isExplicitEmpty(values)) {
      return '';
    }
    
    let finalValue = values ?? '';
    
    // If still empty, check form value
    if (!finalValue) {
      if (isExplicitEmpty(formValue)) return '';
      if (formValue) {
        finalValue = formValue;
        console.log(`[useFormSubmitUtils] Using ${fieldName} from form.getValues():`, finalValue);
      }
    }
    
    // If still empty, check hidden field (but ignore explicit empty)
    if (!finalValue) {
      if (isExplicitEmpty(hiddenValue)) return '';
      if (hiddenValue) {
        finalValue = hiddenValue;
        console.log(`[useFormSubmitUtils] Using ${fieldName} from hidden field:`, finalValue);
      }
    }
    
    return finalValue;
  };

  const updateInterview = async (interviewId: string, updateData: Partial<Interview>): Promise<boolean> => {
    try {
      // Ensure all fields are defined in the update data
      // This prevents "no changes detected" errors in the database service
      if (!updateData.title) updateData.title = '';
      if (!updateData.intervieweeName) updateData.intervieweeName = '';
      if (!updateData.company) updateData.company = '';
      if (!updateData.context) updateData.context = '';
      if (!updateData.question) updateData.question = '';
      if (!updateData.answer) updateData.answer = '';
      
      // CRITICAL FIX: Special handling for tags array to prevent PostgreSQL error
      if ('tags' in updateData) {
        // Make absolutely sure it's a valid array
        if (!Array.isArray(updateData.tags)) {
          updateData.tags = []; // Initialize to empty array if not already an array
        } else {
          // Filter out invalid values
          updateData.tags = updateData.tags.filter(tag => 
            tag !== null && tag !== undefined && typeof tag === 'string'
          );
        }
        
        console.log('[useFormSubmitUtils] Processed tags for database:', 
          JSON.stringify(updateData.tags), 
          'Type:', typeof updateData.tags,
          'Is Array:', Array.isArray(updateData.tags),
          'Length:', updateData.tags.length
        );
      } else {
        // Always include tags even if not provided in the update data
        updateData.tags = [];
      }
      
      // Handle timestamp properly - only set if user explicitly provided a value
      console.log('[useFormSubmitUtils] BEFORE timestamp processing:', {
        'updateData.timestamp': updateData.timestamp,
        'typeof updateData.timestamp': typeof updateData.timestamp,
        'updateData.timestamp === undefined': updateData.timestamp === undefined,
        'updateData.timestamp === null': updateData.timestamp === null,
        'updateData.timestamp === ""': updateData.timestamp === ''
      });
      
      if (updateData.timestamp !== undefined && updateData.timestamp !== null) {
        // Handle empty string as intent to clear
        if (updateData.timestamp === '') {
          updateData.timestamp = null as any;
          console.log('[useFormSubmitUtils] Setting timestamp to null (empty string)');
        } else {
          // Validate and normalize timestamp
          const t = updateData.timestamp as any;
          const asIso = 
            t instanceof Date ? t.toISOString() :
            typeof t === 'number' ? new Date(t).toISOString() :
            typeof t === 'string' ? new Date(t).toISOString() :
            undefined;

          if (asIso && !Number.isNaN(Date.parse(asIso))) {
            updateData.timestamp = asIso;
            console.log('[useFormSubmitUtils] Preserving user-provided timestamp:', updateData.timestamp);
          } else {
            // Invalid input → let backend keep existing value
            delete (updateData as any).timestamp;
            console.log('[useFormSubmitUtils] Invalid timestamp, deleting from updateData');
          }
        }
      } else {
        // Not provided → don't override existing value
        delete (updateData as any).timestamp;
        console.log('[useFormSubmitUtils] No timestamp provided, deleting from updateData');
      }
      
      console.log('[useFormSubmitUtils] AFTER timestamp processing:', {
        'updateData.timestamp': updateData.timestamp,
        'timestamp will be sent': 'timestamp' in updateData
      });
      
      // Always update the updated_at field to current time
      updateData.updated_at = new Date().toISOString();
      
      // Ensure URLs are properly formatted
      if (updateData.contextUrl !== undefined) {
        const trimmed = typeof updateData.contextUrl === 'string' ? updateData.contextUrl.trim() : '';
        if (trimmed === '') {
          // Respect explicit clearing by keeping it as empty string
          updateData.contextUrl = '' as any;
        } else if (!/^(https?:\/\/|ntb:)/i.test(trimmed)) {
          updateData.contextUrl = `https://${trimmed}` as any;
        } else {
          updateData.contextUrl = trimmed as any;
        }
      }
      
      console.log('[useFormSubmitUtils] Final update data being sent to service:', {
        ...updateData,
        hasPhotoUrl: !!updateData.photoUrl,
        photoUrl: updateData.photoUrl || 'No photo URL',
        hasContextUrl: !!updateData.contextUrl,
        contextUrl: updateData.contextUrl || 'No context URL',
        timestamp: updateData.timestamp,
        updated_at: updateData.updated_at,
        tags: JSON.stringify(updateData.tags),
        tagsType: Array.isArray(updateData.tags) ? 'array' : typeof updateData.tags,
        isTagsArray: Array.isArray(updateData.tags),
        tagsLength: Array.isArray(updateData.tags) ? updateData.tags.length : 'n/a'
      });
      
      toast.loading('Sender oppdatering...', { id: 'updating-interview' });
      
      const result = await InterviewManagementFacade.updateInterview(interviewId, updateData);
      
      if (result) {
        // Force refresh all interview data queries 
        const queryKeys = ['pendingInterviews', 'interviews', 'feederInterviews']; 
        
        // First invalidate all queries to mark them as stale
        for (const key of queryKeys) {
          await queryClient.invalidateQueries({ queryKey: [key] });
        }
        
        // Then hard refresh by clearing the cache and forcing refetches
        for (const key of queryKeys) {
          queryClient.removeQueries({ queryKey: [key] });
          await queryClient.refetchQueries({ 
            queryKey: [key],
            type: 'all',
            exact: false
          });
        }
        
        // Force a refetch with a timed approach as well
        setTimeout(() => {
          for (const key of queryKeys) {
            queryClient.refetchQueries({ queryKey: [key], type: 'all' });
          }
        }, 500);
        
        console.log('[useFormSubmitUtils] Update successful, interview updated:', interviewId);
        toast.success('Intervju oppdatert', { id: 'updating-interview' });
        return true;
      } else {
        toast.error('Kunne ikke oppdatere intervju', { id: 'updating-interview' });
        console.error('[useFormSubmitUtils] Interview update returned false');
        return false;
      }
    } catch (error) {
      console.error('[useFormSubmitUtils] Error from InterviewService:', error);
      toast.error('Feil ved oppdatering av intervju', { id: 'updating-interview' });
      return false;
    }
  };

  return {
    logFormValues,
    collectFieldValues,
    updateInterview
  };
};
